import React, { useEffect, useState, useRef, useCallback } from 'react'
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, doc, getDoc } from 'firebase/firestore'
import { GoogleMap, useLoadScript, Marker, InfoWindow, Circle } from '@react-google-maps/api'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

const mapContainerStyle = { width: '100%', height: '100%', borderRadius: '0.75rem' }

interface Location {
  latitude: number
  longitude: number
  timestamp?: string
}

interface CrewMember {
  id: string
  name?: string
  projectName?: string
  assignedProjectId?: string
}

export default function CrewMapPage() {
  const { orgId, selectedProjectId, selectedProject, isAdmin, hasFeature } = useAuth()
  const [crew, setCrew] = useState<CrewMember[]>([])
  const [locations, setLocations] = useState<Record<string, Location>>({})
  const [musterEvents, setMusterEvents] = useState<any[]>([])
  const [activeMuster, setActiveMuster] = useState<any>(null)
  const [elapsed, setElapsed] = useState(0)
  const [radiusMeters, setRadiusMeters] = useState(1609)
  const [selectedCrewId, setSelectedCrewId] = useState<string | null>(null)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (selectedProject?.musterRadiusMeters) {
      setRadiusMeters(selectedProject.musterRadiusMeters)
    } else {
      setRadiusMeters(1609)
    }
  }, [selectedProject])

  useEffect(() => {
    if (!orgId) return

    let crewQuery
    if (selectedProjectId) {
      crewQuery = query(
        collection(db, 'organizations', orgId, 'users'),
        where('status', '==', 'approved'),
        where('assignedProjectId', '==', selectedProjectId)
      )
    } else {
      crewQuery = query(
        collection(db, 'organizations', orgId, 'users'),
        where('status', '==', 'approved')
      )
    }

    const unsubCrew = onSnapshot(crewQuery, snap => {
      setCrew(snap.docs.map(d => ({ id: d.id, ...d.data() } as CrewMember)))
    })

    const unsubLocations = onSnapshot(
      collection(db, 'organizations', orgId, 'locations'),
      snap => {
        const locs: Record<string, Location> = {}
        snap.docs.forEach(d => {
          const data = d.data() as any
          if (data.latitude && data.longitude) {
            locs[d.id] = { latitude: data.latitude, longitude: data.longitude, timestamp: data.timestamp }
          }
        })
        setLocations(locs)
      }
    )

    const unsubMuster = onSnapshot(
      query(
        collection(db, 'organizations', orgId, 'musterEvents'),
        where('projectId', '==', selectedProjectId || '__none__'),
        orderBy('createdAt', 'desc')
      ),
      snap => {
        const active = snap.docs.find(d => d.data().status === 'active')
        if (active) setActiveMuster({ id: active.id, ...active.data() })
        else setActiveMuster(null)
        setMusterEvents(snap.docs.slice(0, 10).map(d => ({ id: d.id, ...d.data() })))
      }
    )

    return () => { unsubCrew(); unsubLocations(); unsubMuster() }
  }, [orgId, selectedProjectId])

  useEffect(() => {
    if (activeMuster) {
      const start = new Date(activeMuster.createdAt).getTime()
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000)
    } else {
      setElapsed(0)
      if (timerRef.current) clearInterval(timerRef.current)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [activeMuster])

  const getMapCenter = useCallback(() => {
    if (selectedProject?.location?.latitude && selectedProject?.location?.longitude) {
      return { lat: selectedProject.location.latitude, lng: selectedProject.location.longitude }
    }
    const tracked = crew
      .map(c => locations[c.id])
      .filter(Boolean)
      .filter((loc: any) => loc.latitude && loc.longitude)
    if (tracked.length === 0) return { lat: 32.5007, lng: -94.7405 } // Longview, TX fallback
    const avgLat = tracked.reduce((sum: number, loc: any) => sum + loc.latitude, 0) / tracked.length
    const avgLng = tracked.reduce((sum: number, loc: any) => sum + loc.longitude, 0) / tracked.length
    return { lat: avgLat, lng: avgLng }
  }, [crew, locations, selectedProject])

  const handleMuster = async () => {
    if (!selectedProjectId || !selectedProject) {
      alert('Select a project from the sidebar before triggering a muster.')
      return
    }
    if (!window.confirm(`Trigger emergency muster for ${selectedProject.name}? This sends push notifications only to project-assigned crew within ${Math.round(radiusMeters / 1609 * 10) / 10} miles.`)) return

    const center = selectedProject.location || getMapCenter()
    const docRef = await addDoc(collection(db, 'organizations', orgId, 'musterEvents'), {
      orgId,
      projectId: selectedProjectId,
      status: 'active',
      center: { latitude: (center as any).latitude, longitude: (center as any).longitude },
      radiusMeters,
      createdBy: 'admin',
      responses: [],
      notifiedUserIds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })
    const snap = await getDoc(doc(db, 'organizations', orgId, 'musterEvents', docRef.id))
    setActiveMuster({ id: docRef.id, ...snap.data() })
  }

  const handleResolve = async () => {
    if (!activeMuster) return
    await updateDoc(doc(db, 'organizations', orgId, 'musterEvents', activeMuster.id), {
      status: 'resolved',
      resolvedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })
    setActiveMuster(null)
    setElapsed(0)
    if (timerRef.current) clearInterval(timerRef.current)
  }

  const updateResponse = async (userId: string, status: 'accounted' | 'needs_help' | 'no_response') => {
    if (!activeMuster) return
    const responses = [...(activeMuster.responses || [])]
    const idx = responses.findIndex(r => r.userId === userId)
    if (idx >= 0) responses[idx] = { ...responses[idx], status, updatedAt: new Date().toISOString() }
    else responses.push({ userId, status, updatedAt: new Date().toISOString() })
    await updateDoc(doc(db, 'organizations', orgId, 'musterEvents', activeMuster.id), { responses, updatedAt: new Date().toISOString() })
  }

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  })

  const formatElapsed = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
  }

  if (!isAdmin()) {
    return (
      <AppLayout title="Crew Map">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="bg-gray-100 rounded-full p-6 mb-6">
            <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Admin Access Only</h3>
          <p className="text-gray-500 max-w-sm">Crew Map and Emergency Muster can only be accessed by organization administrators.</p>
        </div>
      </AppLayout>
    )
  }

  if (!hasFeature('crewmap')) {
    return (
      <AppLayout title="Crew Map">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="bg-gray-100 rounded-full p-6 mb-6">
            <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0121 18.382V7.618a1 1 0 01-.447-.894L15 7m0 13V7m0 0L9.553 4.553A1 1 0 009 4.118v.006" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Enterprise Plan Required</h3>
          <p className="text-gray-500 max-w-sm mb-6">Crew location tracking, live crew map, and emergency muster are available on the Enterprise plan.</p>
          <button className="bg-[#e85d26] text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-[#d14d1a]">
            Upgrade to Enterprise
          </button>
        </div>
      </AppLayout>
    )
  }

  if (!selectedProjectId) {
    return (
      <AppLayout title="Crew Map">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="bg-gray-100 rounded-full p-6 mb-6">
            <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0121 18.382V7.618a1 1 0 01-.447-.894L15 7m0 13V7m0 0L9.553 4.553A1 1 0 009 4.118v.006" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Project</h3>
          <p className="text-gray-500 max-w-sm">Crew Map and Emergency Muster are project-scoped. Select a project from the sidebar to continue.</p>
        </div>
      </AppLayout>
    )
  }

  const responseColors: Record<string, string> = { accounted: '#16a34a', needs_help: '#dc2626', no_response: '#888' }
  const mapCenter = getMapCenter()

  return (
    <AppLayout title={`Crew Map — ${selectedProject?.name || 'Project'}`}>
      <div className="flex gap-6 h-[calc(100vh-140px)]">
        {/* Map Panel */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Live Crew Map</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-sm">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                <span className="text-gray-600">Active</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm">
                <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                <span className="text-gray-600">Stale {'>'}2h</span>
              </div>
            </div>
          </div>

          {loadError && (
            <div className="flex-1 bg-red-50 rounded-xl border border-red-200 flex items-center justify-center">
              <div className="text-center p-6">
                <p className="text-red-700 font-semibold">Failed to load Google Maps</p>
                <p className="text-red-500 text-sm mt-1">{loadError.message}</p>
              </div>
            </div>
          )}
          {!isLoaded && !loadError && (
            <div className="flex-1 bg-gray-50 rounded-xl border border-dashed border-gray-300 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Loading map...</p>
              </div>
            </div>
          )}
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={13}
              center={mapCenter}
              options={{
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
              }}
            >
              {selectedProject?.location && (
                <Circle
                  center={{ lat: selectedProject.location.latitude, lng: selectedProject.location.longitude }}
                  radius={radiusMeters}
                  options={{
                    fillColor: '#e85d26',
                    fillOpacity: 0.1,
                    strokeColor: '#e85d26',
                    strokeOpacity: 0.6,
                    strokeWeight: 2,
                  }}
                />
              )}
              {crew.map(c => {
                const loc = locations[c.id]
                if (!loc || !loc.latitude || !loc.longitude) return null
                const isRecent = loc.timestamp && (Date.now() - new Date(loc.timestamp).getTime()) < 2 * 60 * 60 * 1000
                return (
                  <Marker
                    key={c.id}
                    position={{ lat: loc.latitude, lng: loc.longitude }}
                    title={c.name || 'Unknown'}
                    onClick={() => setSelectedCrewId(c.id)}
                    icon={{
                      url: isRecent
                        ? 'data:image/svg+xml;utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#16a34a" stroke="#fff" stroke-width="2"/></svg>'
                        : 'data:image/svg+xml;utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#9ca3af" stroke="#fff" stroke-width="2"/></svg>',
                      scaledSize: new window.google.maps.Size(24, 24),
                      anchor: new window.google.maps.Point(12, 12),
                    }}
                  />
                )
              })}
              {selectedCrewId && (() => {
                const c = crew.find(x => x.id === selectedCrewId)
                const loc = locations[selectedCrewId]
                if (!c || !loc) return null
                return (
                  <InfoWindow
                    position={{ lat: loc.latitude, lng: loc.longitude }}
                    onCloseClick={() => setSelectedCrewId(null)}
                  >
                    <div className="min-w-[140px]">
                      <p className="font-semibold text-sm text-gray-900">{c.name || 'Unknown'}</p>
                      {c.projectName && <p className="text-xs text-gray-500">{c.projectName}</p>}
                      {loc.timestamp && <p className="text-xs text-gray-400 mt-1">{new Date(loc.timestamp).toLocaleTimeString()}</p>}
                    </div>
                  </InfoWindow>
                )
              })()}
            </GoogleMap>
          )}
        </div>

        {/* Side Panel */}
        <div className="w-96 flex flex-col gap-6">
          {/* Muster Button */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            {activeMuster ? (
              <>
                <div className="text-center mb-4">
                  <p className="text-red-600 font-bold text-lg tracking-wide">MUSTER ACTIVE</p>
                  <p className="text-4xl font-mono font-bold text-gray-900 mt-2">{formatElapsed(elapsed)}</p>
                  <p className="text-sm text-gray-500 mt-1">since alert sent</p>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {(['accounted', 'needs_help', 'no_response'] as const).map(s => {
                    const count = activeMuster.responses?.filter((r: any) => r.status === s).length || 0
                    return (
                      <div key={s} className="text-center p-2 rounded-lg" style={{ backgroundColor: responseColors[s] + '15' }}>
                        <p className="text-2xl font-bold" style={{ color: responseColors[s] }}>{count}</p>
                        <p className="text-xs text-gray-600 capitalize">{s.replace('_', ' ')}</p>
                      </div>
                    )
                  })}
                </div>
                <button onClick={handleResolve} className="w-full bg-green-600 text-white py-2.5 rounded-lg font-semibold hover:bg-green-700 transition-colors">
                  Resolve Muster
                </button>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-600 uppercase mb-1">Muster Radius</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={100}
                      max={16093}
                      step={100}
                      value={radiusMeters}
                      onChange={e => setRadiusMeters(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium text-gray-700 w-20 text-right">{Math.round(radiusMeters / 1609 * 10) / 10} mi</span>
                  </div>
                </div>
                <button onClick={handleMuster} className="w-full bg-red-600 text-white py-3 rounded-lg font-bold text-lg hover:bg-red-700 transition-colors shadow-lg">
                  🚨 EMERGENCY MUSTER
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Notifies only approved crew assigned to {selectedProject?.name} within {Math.round(radiusMeters / 1609 * 10) / 10} miles.
                </p>
              </>
            )}
          </div>

          {/* Crew List with Location */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex-1 flex flex-col overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Crew ({crew.length})</h3>
              <span className="text-xs text-gray-400">{Object.keys(locations).length} tracked</span>
            </div>
            <div className="divide-y divide-gray-100 overflow-y-auto flex-1">
              {crew.map(c => {
                const loc = locations[c.id]
                const response = activeMuster?.responses?.find((r: any) => r.userId === c.id)
                const isRecent = loc && loc.timestamp && (Date.now() - new Date(loc.timestamp).getTime()) < 2 * 60 * 60 * 1000
                return (
                  <div key={c.id} className="px-5 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${isRecent ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{c.name || 'Unknown'}</p>
                          {c.projectName && <p className="text-xs text-gray-400">{c.projectName}</p>}
                          {loc && <p className="text-xs text-gray-400">{new Date(loc.timestamp!).toLocaleTimeString()}</p>}
                        </div>
                      </div>
                      {activeMuster && (
                        <div className="flex gap-1.5">
                          {(['accounted', 'needs_help', 'no_response'] as const).map(s => (
                            <button key={s} onClick={() => updateResponse(c.id, s)}
                              className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors ${response?.status === s ? 'border-transparent' : 'border-gray-300 hover:border-gray-400'}`}
                              style={response?.status === s ? { backgroundColor: responseColors[s], borderColor: responseColors[s] } : {}}
                              title={s.replace('_', ' ')}
                            >
                              <span className="text-xs text-white font-bold">{s === 'accounted' ? '✓' : s === 'needs_help' ? '!' : '?'}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
